import type {
  ApiResponse,
  AttendanceExportScope,
  ManualBillingReportCategory,
  ManualBillingReportResult,
  Organization,
  OrganizationSport,
  OrgAmenityKey,
  Region,
} from '@court-sync/shared';
import { apiClient, apiGet, apiPatch, apiPut } from './apiClient';

export interface UpdateOrganizationPayload {
  name?: string;
  slug?: string;
  subdomain?: string | null;
  subdomainEnabled?: boolean;
  directoryListed?: boolean;
  description?: string | null;
  logoUrl?: string | null;
  bannerUrl?: string | null;
  galleryUrls?: string[];
  address?: string | null;
  phone?: string | null;
  email?: string | null;
  website?: string | null;
  facebook?: string | null;
  instagram?: string | null;
  twitter?: string | null;
  linkedin?: string | null;
  primaryColor?: string | null;
  secondaryColor?: string | null;
  country?: string | null;
  region?: Region | null;
  stateOrProvince?: string | null;
  timezone?: string | null;
  websiteEmbedEnabled?: boolean;
  embedAllowedOrigins?: string[];
  showCoachesOnPublicPage?: boolean;
  amenities?: OrgAmenityKey[];
}

export const organizationService = {
  async getMyOrganization(): Promise<ApiResponse<Organization>> {
    return apiGet<Organization>('/organizations/me');
  },

  async updateOrganization(
    organizationId: string,
    data: UpdateOrganizationPayload
  ): Promise<ApiResponse<Organization>> {
    return apiPatch<Organization>(`/organizations/${organizationId}`, data);
  },

  async checkSubdomainAvailability(
    label: string,
    excludeOrganizationId?: string
  ): Promise<ApiResponse<{ available: boolean; label: string }>> {
    const params = excludeOrganizationId
      ? `?excludeOrganizationId=${encodeURIComponent(excludeOrganizationId)}`
      : '';
    return apiGet<{ available: boolean; label: string }>(
      `/public/tenants/subdomain/${encodeURIComponent(label)}/check${params}`
    );
  },

  async getOrganizationSports(organizationId: string): Promise<ApiResponse<OrganizationSport[]>> {
    return apiGet<OrganizationSport[]>(`/organizations/${organizationId}/sports`);
  },

  async updateOrganizationSports(
    organizationId: string,
    sports: Array<{
      sportId: string;
      customLabel?: string | null;
      isPrimary?: boolean;
      isActive?: boolean;
    }>
  ): Promise<ApiResponse<null>> {
    return apiPut<null>(`/organizations/${organizationId}/sports`, { sports });
  },

  async getManualBillingReport(
    organizationId: string,
    options: {
      academyId?: string;
      clubId?: string;
      category?: ManualBillingReportCategory;
      termId?: string;
      lessonId?: string;
      lessonCategory?: AttendanceExportScope['category'];
      periodStart?: string;
      periodEnd?: string;
      billByAttendance?: boolean;
      page?: number;
      limit?: number;
    }
  ): Promise<ApiResponse<ManualBillingReportResult>> {
    const params: Record<string, string | number> = {};
    if (options.academyId) params.academyId = options.academyId;
    if (options.clubId) params.clubId = options.clubId;
    if (options.category) params.category = options.category;
    if (options.termId) params.termId = options.termId;
    if (options.lessonId) params.lessonId = options.lessonId;
    if (options.lessonCategory) params.lessonCategory = options.lessonCategory;
    if (options.periodStart) params.periodStart = options.periodStart;
    if (options.periodEnd) params.periodEnd = options.periodEnd;
    if (options.billByAttendance) params.billByAttendance = 'true';
    if (options.page !== undefined) params.page = options.page;
    if (options.limit !== undefined) params.limit = options.limit;
    return apiGet<ManualBillingReportResult>(
      `/organizations/${organizationId}/manual-billing-report`,
      { params: Object.keys(params).length ? params : undefined }
    );
  },

  async exportManualBillingReportCsv(
    organizationId: string,
    options: {
      academyId?: string;
      clubId?: string;
      category?: ManualBillingReportCategory;
      termId?: string;
      lessonId?: string;
      lessonCategory?: AttendanceExportScope['category'];
      periodStart?: string;
      periodEnd?: string;
      billByAttendance?: boolean;
    }
  ): Promise<globalThis.Blob> {
    const params: Record<string, string> = { format: 'csv' };
    if (options.academyId) params.academyId = options.academyId;
    if (options.clubId) params.clubId = options.clubId;
    if (options.category) params.category = options.category;
    if (options.termId) params.termId = options.termId;
    if (options.lessonId) params.lessonId = options.lessonId;
    if (options.lessonCategory) params.lessonCategory = options.lessonCategory;
    if (options.periodStart) params.periodStart = options.periodStart;
    if (options.periodEnd) params.periodEnd = options.periodEnd;
    if (options.billByAttendance) params.billByAttendance = 'true';
    const response = await apiClient.get(
      `/organizations/${organizationId}/manual-billing-report`,
      { params, responseType: 'blob' }
    );
    return response.data;
  },
};
